import { Stack } from "@mui/material";
import { useAtomValue } from "jotai";
import type { FC } from "react";
import { useResolvingNowQuery } from "web_myplann/Api/NotificationCenter/useResolvingNowQuery";
import { useUpcomingTimelineQuery } from "web_myplann/Api/NotificationCenter/useUpcomingTimelineQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { timelineFilterAtom } from "web_myplann/NotificationCenter/Atoms/timelineFilterAtom";
import { EmptyTimelineState } from "web_myplann/NotificationCenter/Components/EmptyTimelineState";
import { ResolvingNowSection } from "web_myplann/NotificationCenter/Components/ResolvingNowSection";
import { TimelineGroup } from "web_myplann/NotificationCenter/Components/TimelineGroup";
import { useGroupUpcomingItems } from "web_myplann/NotificationCenter/Hooks/useGroupUpcomingItems";
import { useNavigateFromResolvingNowItem } from "web_myplann/NotificationCenter/Hooks/useNavigateFromResolvingNowItem";
import { useNavigateFromTimelineItem } from "web_myplann/NotificationCenter/Hooks/useNavigateFromTimelineItem";
import { NOTIFICATION_CENTER_ORIGIN } from "web_myplann/NotificationCenter/Utils/notificationOrigin";
import { EmptyListSkeleton } from "web_myplann/Shared/Components/EmptyListSkeleton";
import NotificationCenterError from "./NotificationCenterError";

export const UpcomingTab: FC = () => {
	const { t } = useResource();
	const { horizon, upcomingType } = useAtomValue(timelineFilterAtom);
	const upcomingQuery = useUpcomingTimelineQuery({
		horizon,
		type: upcomingType,
	});
	const resolvingNowQuery = useResolvingNowQuery();
	const navigateToItem = useNavigateFromTimelineItem({
		origin: NOTIFICATION_CENTER_ORIGIN,
	});
	const navigateToResolvingItem = useNavigateFromResolvingNowItem({
		origin: NOTIFICATION_CENTER_ORIGIN,
	});

	const upcomingItems = upcomingQuery.data?.items ?? [];
	const resolvingNow = resolvingNowQuery.data?.items ?? [];

	const timeGroups = useGroupUpcomingItems(upcomingItems, horizon);

	const isLoading =
		(upcomingQuery.isLoading && !upcomingQuery.data) ||
		(resolvingNowQuery.isLoading && !resolvingNowQuery.data);
	const isError = upcomingQuery.isError || resolvingNowQuery.isError;

	if (isLoading) {
		return (
			<EmptyListSkeleton
				label={t(Resources.NotificationCenter.Title)}
				description={t(Resources.NotificationCenter.Tabs.Upcoming)}
			/>
		);
	}

	if (isError) {
		const handleRetry = () => {
			void upcomingQuery.refetch();
			void resolvingNowQuery.refetch();
		};

		return <NotificationCenterError handleRetry={handleRetry} />;
	}

	const isEmpty = resolvingNow.length === 0 && timeGroups.length === 0;
	if (isEmpty) {
		return <EmptyTimelineState variant="upcoming" />;
	}

	return (
		<Stack gap={0} data-testid="notification-center-upcoming-content">
			<ResolvingNowSection
				items={resolvingNow}
				onItemClick={navigateToResolvingItem}
			/>
			{timeGroups.map((group) => (
				<TimelineGroup
					key={group.bucketKey}
					group={group}
					onItemClick={navigateToItem}
				/>
			))}
		</Stack>
	);
};
